import { gql, useQuery, ApolloClient, NormalizedCacheObject, ApolloError } from '@apollo/client';
import { useAuth } from '../auth/useAuth';
import { useState, useEffect, useCallback } from 'react';
import { LearningActivity } from '../../common/types';
import { shouldQueryRegistrationCounter } from '../../common';
import { LEARNING_ACTIVITY_PARAMS, isLocalizedCourseCatalogItemEnabled } from '../../common/constants';
import { useLogger } from '@amzn/xb-hooks';
import { PAGINATED_LEARNING_ACTIVITIES_ERROR, PAGINATED_LEARNING_ACTIVITIES_SUCCESS } from '../../common/rumMetrics';

// Paginated learning activities query
export const LEARNING_ACTIVITIES_PAGINATED = gql`
    query learningActivitiesPaginated($activityAudience: [String!], $program: [String!], $from: Int!, $size: Int!, $queryRegistrationCounter: Boolean = false, $queryCourseCatalogItemLimited: Boolean = false, $queryLocalizedCourseCatalogItem: Boolean = false) {
        learningActivities(learningActivitiesInput: {from: $from, size: $size, activityAudience: $activityAudience, program: $program}) {
            ${LEARNING_ACTIVITY_PARAMS}
        }
    }
`;

interface UsePaginatedLearningActivitiesOptions {
    pageSize?: number;
    enabled?: boolean;
}

interface PaginatedLearningActivitiesResult {
    data: { learningActivities: LearningActivity[] };
    loading: boolean;
    error: Error | null;
    hasMore: boolean;
    loadMore: () => void;
    allLearningActivities: LearningActivity[];
    refetch: () => void;
}

export const usePaginatedLearningActivities = (options: UsePaginatedLearningActivitiesOptions = {}): PaginatedLearningActivitiesResult => {
    const { log, count } = useLogger();
    const { pageSize = 100, enabled = true } = options;
    const { user } = useAuth();
    const [allLearningActivities, setAllLearningActivities] = useState<LearningActivity[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [hasStartedFetching, setHasStartedFetching] = useState(false);

    // Function to fetch all pages recursively
    const fetchAllPages = useCallback(
        async (apolloClient: ApolloClient<NormalizedCacheObject>) => {
            if (!enabled || !user.id) {
                return;
            }

            setIsLoading(true);
            setError(null);
            setAllLearningActivities([]);

            const allActivities: LearningActivity[] = [];
            let currentPage = 0;
            let hasMore = true;

            try {
                while (hasMore) {
                    const variables = {
                        from: currentPage * pageSize,
                        size: pageSize,
                        activityAudience: ['Partner Public Unlisted', 'Internal', 'Internal Private', 'Internal Public'],
                        queryRegistrationCounter: shouldQueryRegistrationCounter(user),
                        queryLocalizedCourseCatalogItem: isLocalizedCourseCatalogItemEnabled(),
                    };

                    try {
                        const result = await apolloClient.query({
                            query: LEARNING_ACTIVITIES_PAGINATED,
                            variables,
                            fetchPolicy: 'network-only',
                        });

                        const newActivities = result.data?.learningActivities || [];

                        allActivities.push(...newActivities);

                        // Check if we have more data to load
                        // Stop if we received fewer items than requested
                        hasMore = newActivities.length === pageSize;
                        currentPage++;
                    } catch (queryError: unknown) {
                        // Check if the error message indicates no more learning activities
                        const errorMessage = (queryError as ApolloError)?.message || '';
                        if (errorMessage.includes('Could not find any learning activities for given query')) {
                            hasMore = false;
                        } else {
                            // Re-throw other errors
                            throw queryError;
                        }
                    }
                }

                log.info('Finished fetching all pages. Total activities:', allActivities.length);
                count(PAGINATED_LEARNING_ACTIVITIES_SUCCESS);
                setAllLearningActivities(allActivities);
            } catch (err) {
                // Suppress auth errors from IltRegistrationManagementService for PARTNERS_B2I users
                // who can see cross-org classes via link sharing but aren't authorized for registration counters
                const errorMessage = (err as Error)?.message || '';
                const isPartnerB2I = user.organizationType === 'PARTNERS_B2I';
                if (isPartnerB2I && errorMessage.toLowerCase().includes('not authorized')) {
                    log.warn('Suppressed auth error for cross-org registration counter', err as Error);
                    setAllLearningActivities(allActivities);
                } else {
                    setError(err as Error);
                    log.error('Error retrieving paginated learning activities', err as Error);
                    count(PAGINATED_LEARNING_ACTIVITIES_ERROR);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [enabled, user.id, pageSize],
    );

    // Use a dummy query to get access to the Apollo client
    const { client: apolloClient } = useQuery(LEARNING_ACTIVITIES_PAGINATED, {
        skip: true, // Skip the actual query execution
    });

    // Start fetching when conditions are met
    useEffect(() => {
        if (enabled && user.id && apolloClient && !hasStartedFetching) {
            setHasStartedFetching(true);
            fetchAllPages(apolloClient);
        }
    }, [enabled, user.id, apolloClient, hasStartedFetching, fetchAllPages]);

    // Reset state when user changes
    useEffect(() => {
        if (!user.id) {
            setAllLearningActivities([]);
            setIsLoading(false);
            setError(null);
            setHasStartedFetching(false);
        }
    }, [user.id]);

    const refetch = useCallback(() => {
        if (apolloClient) {
            setHasStartedFetching(false);
            // This will trigger the useEffect to start fetching again
        }
    }, [apolloClient]);

    // Dummy loadMore function for compatibility
    const loadMore = useCallback(() => {
        // No-op since we fetch all data automatically
    }, []);

    return {
        data: { learningActivities: allLearningActivities },
        loading: isLoading,
        error,
        hasMore: false, // Always false since we fetch all data
        loadMore,
        allLearningActivities,
        refetch,
    };
};
